import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const EmergencyAccess = () => {
  const [email, setEmail] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessData, setAccessData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/emergency-access`, { email, keyword });
      setAccessData(res.data);
      toast.success('Emergency access granted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const url = `${API}/emergency-access/document/${doc.id}?token=${accessData.document_download_token}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Failed to download document');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (accessData) {
    return (
      <div className="dashboard-container" data-testid="emergency-access-view">
        <nav className="dashboard-nav">
          <Link to="/"><img src="/careguard-logo.png" alt="CareGuard" className="nav-logo" /></Link>
          <div className="nav-links">
            <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '0.85rem' }}>
              <i className="fa-solid fa-shield-halved"></i> EMERGENCY ACCESS — READ ONLY
            </span>
          </div>
        </nav>

        <div className="dashboard-content page-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '12px', padding: '1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: '#ef4444', fontSize: '1.25rem' }}></i>
            <div>
              <strong style={{ color: '#dc2626' }}>Emergency Read-Only Access</strong>
              <p style={{ margin: '0.25rem 0 0', color: '#991b1b', fontSize: '0.85rem' }}>
                You are viewing {accessData.caregiver_name}'s information as an emergency delegate. This access is logged.
              </p>
            </div>
          </div>

          <h1 className="page-title" data-testid="ea-title">
            <i className="fa-solid fa-user-shield" style={{ marginRight: '10px', color: '#2c5364' }}></i>
            {accessData.caregiver_name}'s Information
          </h1>

          {/* Profile Info */}
          {accessData.profile && (
            <div className="section-card" style={{ marginBottom: '1.5rem' }} data-testid="ea-profile">
              <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-user" style={{ marginRight: '8px' }}></i>Profile Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                {Object.entries(accessData.profile).filter(([,v]) => v).map(([k, v]) => (
                  <div key={k} style={{ padding: '0.5rem 0.75rem', background: '#f8f9fa', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b8899', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                    <div style={{ fontWeight: '600', color: '#2c5364' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legal Documents */}
          {accessData.legal_documents && (
            <div className="section-card" style={{ marginBottom: '1.5rem' }} data-testid="ea-documents">
              <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-file-lines" style={{ marginRight: '8px' }}></i>Legal Documents</h2>
              {accessData.legal_documents.length === 0 ? (
                <p style={{ color: '#94a3b8' }}>No documents uploaded</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {accessData.legal_documents.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #2c5364' }}>
                      <div>
                        <strong style={{ color: '#2c5364' }}>{doc.file_name}</strong>
                        <div style={{ fontSize: '0.8rem', color: '#6b8899' }}>
                          {doc.document_type?.replace(/_/g, ' ')} | Uploaded: {formatDate(doc.uploaded_at)}
                          {doc.expiration_date && <span> | Expires: {formatDate(doc.expiration_date)}</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDownload(doc)} className="submit-button" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.85rem' }} data-testid={`ea-download-${doc.id}`}>
                        <i className="fa-solid fa-download"></i> Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Client Info */}
          {accessData.clients && (
            <div className="section-card" style={{ marginBottom: '1.5rem' }} data-testid="ea-clients">
              <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-user-group" style={{ marginRight: '8px' }}></i>Client Information</h2>
              {accessData.clients.length === 0 ? (
                <p style={{ color: '#94a3b8' }}>No clients</p>
              ) : (
                accessData.clients.map(c => (
                  <div key={c.id} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#2c5364' }}>{c.name}</strong>
                    {c.health_condition && <div style={{ fontSize: '0.85rem', color: '#6b8899' }}>Condition: {c.health_condition}</div>}
                    {c.address && <div style={{ fontSize: '0.85rem', color: '#6b8899' }}>Address: {c.address}</div>}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Vitals History */}
          {accessData.vitals && (
            <div className="section-card" data-testid="ea-vitals">
              <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-heart-pulse" style={{ marginRight: '8px' }}></i>Recent Vitals</h2>
              {accessData.vitals.length === 0 ? (
                <p style={{ color: '#94a3b8' }}>No vitals recorded</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem', color: '#6b8899' }}>Date</th>
                        <th style={{ padding: '0.5rem', color: '#6b8899' }}>BP</th>
                        <th style={{ padding: '0.5rem', color: '#6b8899' }}>HR</th>
                        <th style={{ padding: '0.5rem', color: '#6b8899' }}>Temp</th>
                        <th style={{ padding: '0.5rem', color: '#6b8899' }}>O2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessData.vitals.map((v, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '0.5rem', color: '#2c5364' }}>{formatDate(v.timestamp)}</td>
                          <td style={{ padding: '0.5rem' }}>{v.blood_pressure_systolic || '-'}/{v.blood_pressure_diastolic || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{v.heart_rate || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{v.temperature || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{v.oxygen_saturation || '-'}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" data-testid="emergency-access-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/careguard-logo-full.jpg" alt="CareGuard" style={{ height: '120px', marginBottom: '1rem', borderRadius: '8px' }} />
          <h1 style={{ color: '#dc2626' }}>Emergency Access</h1>
          <p style={{ color: '#6b8899' }}>For authorized emergency delegates only</p>
        </div>

        <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', borderLeft: '4px solid #ef4444' }}>
          <p style={{ margin: 0, color: '#991b1b', fontSize: '0.85rem' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px' }}></i>
            This portal provides read-only access to a caregiver's critical information. All access is logged and monitored.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="ea-email">Caregiver's Email</label>
            <input id="ea-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="caregiver@email.com" data-testid="ea-email-input" />
          </div>
          <div className="form-group">
            <label htmlFor="ea-keyword">Emergency Access Keyword</label>
            <input id="ea-keyword" type="password" value={keyword} onChange={e => setKeyword(e.target.value)} required placeholder="Enter the secret keyword" data-testid="ea-keyword-input" />
          </div>
          <button type="submit" className="submit-button" disabled={loading} data-testid="ea-submit-btn" style={{ background: '#dc2626' }}>
            {loading ? 'Verifying...' : 'Access Emergency Information'}
          </button>
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link to="/" style={{ color: '#2c5364', textDecoration: 'none' }}>Back to Home</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmergencyAccess;
